import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";

interface ICP {
  pathPrefix?: string;
  commonService: CommonService;
}

// Core ICP data types matching Joi schema
interface ICPDemographics {
  size: {
    employees: string;
    locations: number;
    growthRate: string;
  };
  revenue: {
    annual: string;
    growth: string;
    profitMargin: string;
  };
  funding: {
    stage: string;
    totalRaised: string;
    lastRound: string;
  };
  geography: {
    headquarters: string;
    targetMarkets: string[];
    expansion: string[];
  };
}

interface ICPTechnical {
  stack: {
    primary: string[];
    integrations: string[];
    infrastructure: string[];
  };
  security: {
    requirements: string[];
    certifications: string[];
    compliance: string[];
  };
  development: {
    methodology: string;
    teamSize: string;
    tooling: string[];
  };
}

interface ICPBusiness {
  industry: {
    primary: string;
    secondary: string[];
    verticals: string[];
  };
  maturity: {
    stage: string;
    yearsInBusiness: number;
    marketPosition: string;
  };
  operations: {
    model: string;
    distribution: string[];
    partnerships: string[];
  };
}

interface ICPBuying {
  stakeholders: {
    decisionMaker: {
      role: string;
      responsibilities: string[];
      painPoints: string[];
    };
    influencers: {
      roles: string[];
      priorities: string[];
    };
  };
  timeline: {
    average: string;
    factors: string[];
    seasonality: string;
  };
  budget: {
    range: string;
    cycle: string;
    approval: string;
  };
}

interface ICPCompetitive {
  current: {
    solution: string;
    limitations: string[];
    switchingCosts: string;
  };
  alternatives: {
    considered: string[];
    differentiators: string[];
  };
  market: {
    trends: string[];
    challenges: string[];
  };
}

interface ICPSuccess {
  primary: {
    metric: string;
    target: string;
    timeframe: string;
  };
  roi: {
    financial: string;
    operational: string;
    timeline: string;
  };
  outcomes: string[];
}

interface ICPData {
  userId: string;
  name: string;
  project_id: string;
  status?: "draft" | "active" | "archived";
  score?: number;
  demographics: ICPDemographics;
  technical: ICPTechnical;
  business: ICPBusiness;
  buying: ICPBuying;
  competitive: ICPCompetitive;
  success: ICPSuccess;
  notes?: string | null;
}

interface GetICPsParams {
  userId: string;
  name?: string;
  status?: "draft" | "active" | "archived";
  sortBy?: "createdAt";
  sortOrder?: "asc" | "desc";
  limit?: number;
  page?: number;
}

interface GetICPParams {
  userId: string;
  fields?: string;
}

interface StartResearchParams {
  userId: string;
  modelAPIKey: string;
  socketId?: string;
  priority?: number;
  customInstructions?: string;
}

interface GetActivityLogParams {
  projectId: string;
  jobId?: string;
  page?: number;
  limit?: number;
}

class ICPService implements ICP {
  pathPrefix?: string;
  commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/projects";
    this.commonService = new CommonService();
  }

  // Get all ICPs for a project
  getICPs(projectId: string, params: GetICPsParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/icps`,
      params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get a specific ICP
  getICP(projectId: string, icpId: string, params: GetICPParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/icps/${icpId}`,
      params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Update an ICP
  updateICP(projectId: string, icpId: string, icpData: Partial<ICPData>) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${this.pathPrefix}/${projectId}/icps/${icpId}`,
      data: icpData,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Delete an ICP
  deleteICP(projectId: string, icpId: string, userId: string) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${projectId}/icps/${icpId}`,
      params: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Start ICP research
  startResearch(projectId: string, params: StartResearchParams) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/icps/research`,
      data: params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get research status
  getResearchStatus(projectId: string, userId: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/icps/status`,
      params: { userId },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Get activity log
  getActivityLog(projectId: string | any, params: GetActivityLogParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/icps/activity${
        params.jobId ? `/${params.jobId}` : ""
      }`,
      params: {
        page: params.page,
        limit: params.limit,
      },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Gets the answer for a question on a given document
  async answerQuestionOnDocument(
    userId: string | any,
    projectId: string | any,
    icpId: string | any,
    question: string,
    modelAPIKey: string | any
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/icps/${icpId}/qa`,
      data: { question, userId, modelAPIKey },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }
}

export default ICPService;
